import React, { useState, useEffect } from "react";
import Navbar from "../frontend/backend/navbar.js";
import Footer from "../frontend/backend/footer.js";
import Chatbox from "../frontend/backend/chatbox.js";
import HeaderRight from "../frontend/backend/header_right.js";
import LeftSidebar from "../frontend/backend/leftSidebar.js";
import axios from "axios";
import apiConfig from '../apiConfig';


const gatewayTabs = [
  { key: "okaypay", label: "OkayPay" },
  { key: "okexpay", label: "OkayXPay" },
  { key: "winypay", label: "WinyPay" },
  { key: "manual",  label: "Manual Pay" },
];

// Field schema per gateway type — okaypay/okexpay share the same shape
// (mch_id/key/host), winypay has a different credential set.
const fieldSchema = {
  okaypay: [
    { name: "okpay_mch_id", label: "MCH ID" },
    { name: "okpay_key", label: "Secret Key" },
    { name: "okpay_host", label: "Host URL", placeholder: "https://api.wpay.one" },
  ],
  okexpay: [
    { name: "okpay_mch_id", label: "MCH ID" },
    { name: "okpay_key", label: "Secret Key" },
    { name: "okpay_host", label: "Host URL", placeholder: "https://api.wpay.life" },
  ],
  winypay: [
    { name: "winypay_merchant_code", label: "Merchant Code" },
    { name: "winypay_secret_key", label: "Secret Key (deposit)" },
    { name: "winypay_payout_key", label: "Payout Key (withdraw)" },
    { name: "winypay_host", label: "Host URL", placeholder: "https://bd.gopostman.com" },
  ],
  manual: [], // no credentials — enabled/disabled only
};

const emptyFormFor = (gatewayKey) => {
  const fields = fieldSchema[gatewayKey] || [];
  const base = { withdrawMode: "Auto" };
  fields.forEach((f) => { base[f.name] = ""; });
  return base;
};

const GatewayManage = () => {
  const [activeTab, setActiveTab] = useState("okaypay");
  const [inpval, setINP] = useState(emptyFormFor("okaypay"));
  const [settingId, setSettingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(true); // used only for Manual tab

  const [globalLoading, setGlobalLoading] = useState(false);
  const [manualEnabled, setManualEnabled] = useState(true);
  const [activeWithdrawGateway, setActiveWithdrawGateway] = useState("okaypay");

  // ---- Load global settings (manual toggle + active withdraw gateway) once ----
  useEffect(() => {
    axios.get(`${apiConfig.baseURL}/api/users/GatewayGlobalSettingGet`)
      .then((res) => {
        if (res.data) {
          setManualEnabled(res.data.manualEnabled ?? true);
          setActiveWithdrawGateway(res.data.activeWithdrawGateway || "okaypay");
        }
      })
      .catch((err) => console.error("Failed to load global gateway settings:", err));
  }, []);

  // ---- Load the selected gateway's settings whenever the tab changes ----
  useEffect(() => {
    if (activeTab === "manual") {
      // Manual has no per-row credentials — reuse the global manual toggle.
      setSettingId(null);
      setEnabled(manualEnabled);
      return;
    }

    setLoading(true);
    axios.get(`${apiConfig.baseURL}/api/users/GatewaySettingGet`, { params: { status: activeTab } })
      .then((res) => {
        if (res.data) {
          setSettingId(res.data._id || null);
          const fields = fieldSchema[activeTab] || [];
          const loaded = { withdrawMode: res.data.withdrawMode || "Auto" };
          fields.forEach((f) => { loaded[f.name] = res.data[f.name] || ""; });
          setINP(loaded);
        } else {
          setSettingId(null);
          setINP(emptyFormFor(activeTab));
        }
      })
      .catch((err) => {
        console.error("Failed to load settings:", err);
        setSettingId(null);
        setINP(emptyFormFor(activeTab));
      })
      .finally(() => setLoading(false));
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const setdata = (e) => {
    const { name, value } = e.target;
    setINP((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fields = fieldSchema[activeTab] || [];
    for (const f of fields) {
      if (!inpval[f.name]) return alert(`${f.label} is required`);
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${apiConfig.baseURL}/api/users/GatewaySettingUpdate`,
        { ...inpval, status: activeTab }
      );
      if (res.status === 200) {
        alert(`${gatewayTabs.find((g) => g.key === activeTab)?.label} settings updated successfully!`);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update settings.");
    } finally {
      setLoading(false);
    }
  };

  const handleManualToggleSave = async () => {
    setGlobalLoading(true);
    try {
      await axios.post(`${apiConfig.baseURL}/api/users/GatewayGlobalSettingUpdate`, {
        manualEnabled: enabled,
        activeWithdrawGateway,
      });
      setManualEnabled(enabled);
      alert("Manual Pay availability updated!");
    } catch (err) {
      console.error(err);
      alert("Failed to update Manual Pay setting.");
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleWithdrawGatewaySave = async () => {
    setGlobalLoading(true);
    try {
      await axios.post(`${apiConfig.baseURL}/api/users/GatewayGlobalSettingUpdate`, {
        manualEnabled,
        activeWithdrawGateway,
      });
      alert("Active withdraw gateway updated!");
    } catch (err) {
      console.error(err);
      alert("Failed to update withdraw gateway.");
    } finally {
      setGlobalLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Chatbox />
      <HeaderRight />
      <LeftSidebar />

      <div className="content-body">
        <div className="container-fluid">

          {/* Global controls: withdraw gateway + manual pay toggle */}
          <div className="col-lg-12 mb-4">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Global Gateway Controls</h4>
              </div>
              <div className="card-body">

                <div className="mb-3 row align-items-center">
                  <label className="col-sm-3 col-form-label">Active Withdraw Gateway</label>
                  <div className="col-sm-6">
                    <select
                      className="form-control"
                      value={activeWithdrawGateway}
                      onChange={(e) => setActiveWithdrawGateway(e.target.value)}
                    >
                      <option value="okaypay">OkayPay</option>
                      <option value="okexpay">OkayXPay</option>
                      <option value="winypay">WinyPay</option>
                    </select>
                    <small className="text-muted">
                      Withdrawals always go through this gateway, regardless of which channel the user deposited with.
                    </small>
                  </div>
                  <div className="col-sm-3">
                    <button type="button" className="btn btn-primary" disabled={globalLoading} onClick={handleWithdrawGatewaySave}>
                      {globalLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>

                <div className="mb-3 row align-items-center">
                  <label className="col-sm-3 col-form-label">Manual Pay Channel</label>
                  <div className="col-sm-6">
                    <select
                      className="form-control"
                      value={enabled ? "on" : "off"}
                      onChange={(e) => setEnabled(e.target.value === "on")}
                    >
                      <option value="on">Enabled</option>
                      <option value="off">Disabled</option>
                    </select>
                    <small className="text-muted">
                      Disabled hides the "Manual Pay" channel chip on the deposit page.
                    </small>
                  </div>
                  <div className="col-sm-3">
                    <button type="button" className="btn btn-primary" disabled={globalLoading} onClick={handleManualToggleSave}>
                      {globalLoading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Per-gateway credential settings */}
          <div className="col-lg-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Payment Gateway Settings</h4>
                <ul className="nav nav-tabs mt-3">
                  {gatewayTabs.map((tab) => (
                    <li className="nav-item" key={tab.key}>
                      <button
                        type="button"
                        className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                        style={{ border: "none", background: "none", cursor: "pointer" }}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card-body">
                {activeTab === "manual" ? (
                  <div className="mb-3">
                    <p className="text-muted">
                      Manual Pay has no gateway credentials — deposits are approved by an admin.
                      Use the "Manual Pay Channel" toggle above to show/hide it on the deposit page.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    {(fieldSchema[activeTab] || []).map((f) => (
                      <div className="mb-3 row" key={f.name}>
                        <label className="col-sm-3 col-form-label">{f.label}</label>
                        <div className="col-sm-9">
                          <input
                            type="text"
                            name={f.name}
                            className="form-control"
                            placeholder={f.placeholder || `Enter ${f.label}`}
                            value={inpval[f.name] || ""}
                            onChange={setdata}
                          />
                        </div>
                      </div>
                    ))}

                    <div className="mb-3 row">
                      <label className="col-sm-3 col-form-label">Withdraw Mode</label>
                      <div className="col-sm-9">
                        <select
                          name="withdrawMode"
                          className="form-control"
                          value={inpval.withdrawMode}
                          onChange={setdata}
                        >
                          <option value="Auto">Auto</option>
                          <option value="Manual">Manual</option>
                        </select>
                        <small className="text-muted">
                          Auto: Processed automatically. Manual: Requires admin approval.
                        </small>
                      </div>
                    </div>

                    <div className="mb-3 row">
                      <div className="col-sm-10">
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                          {loading ? "Saving..." : `Update ${gatewayTabs.find((g) => g.key === activeTab)?.label}`}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
};

export default GatewayManage;